import React, { useState, useEffect } from "react";
import { Button, Input, Select, Table, message, Radio, Switch } from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileExcelOutlined,
  MailOutlined,
  PlusOutlined,
  UnorderedListOutlined,
  UploadOutlined,
  UserAddOutlined,
  UserOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  WhatsAppOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import AudienceSourceCard from "./AudienceSourceCard";
import { ValidationCard, ChannelBadge } from "./AudienceFlowShared";
import {
  columnOptions,
  formatFileSize,
  makeDetectedRowsText,
  previewRows,
  sampleCsvHref,
  statChips,
  validationCards,
} from "./audienceFlowData";
import { getImportPreview } from "../../../services/audience.service";

const FolderUploadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline-block" }}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const ExcelFileIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline-block" }}>
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M8 13h8M8 17h8" />
  </svg>
);

export const AudienceSourceStep = ({ 
  options, 
  selectedSource, 
  onSelect,
  isGmailConnected,
  isOutlookConnected,
  onDisconnect,
  onConnect
}) => {
  const isGoogle = selectedSource === "google";
  const isOutlook = selectedSource === "outlook";
  const showStatusPanel = isGoogle || isOutlook;
  const isConnected = isGoogle ? isGmailConnected : isOutlookConnected;

  return (
    <div className="tw-mx-auto tw-mt-4 tw-max-w-[985px]">
      <div className="tw-mb-5">
        <h2 className="tw-m-0 tw-text-[18px] tw-font-semibold tw-text-slate-800 tw-font-poppins">
          Choose how you want to add contacts
        </h2>
        <p className="tw-m-0 tw-mt-1 tw-text-[13.5px] tw-font-normal tw-text-slate-500 tw-font-inter">
          You can always add more contacts later.
        </p>
      </div>

      <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
        {options.map((option) => (
          <AudienceSourceCard
            key={option.id}
            option={option}
            selected={selectedSource === option.id}
            onSelect={onSelect}
          />
        ))}
      </div>

      {/* Premium Integration Status Panel */}
      {showStatusPanel && (
        <div className="tw-mt-6 tw-p-5 tw-rounded-2xl tw-border tw-border-solid tw-border-[#5D44FF]/15 tw-bg-[#5D44FF]/5 tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4 tw-transition-all tw-duration-350 animate-fade-in">
          <div className="tw-flex-1">
            <div className="tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
              <h4 className="tw-m-0 tw-text-[15px] tw-font-bold tw-text-text-primary tw-font-poppins">
                {isGoogle ? "Google Contacts Integration" : "Outlook Contacts Integration"}
              </h4>
              <span className={`tw-inline-flex tw-items-center tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-font-inter tw-border tw-border-solid ${
                isConnected
                  ? "tw-bg-emerald-50 tw-text-emerald-700 tw-border-emerald-200"
                  : "tw-bg-slate-100 tw-text-sub-text tw-border-slate-200"
              }`}>
                {isConnected ? "Connected" : "Disconnected"}
              </span>
            </div>
            <p className="tw-m-0 tw-mt-1.5 tw-text-[12.5px] tw-text-sub-text tw-font-medium tw-font-inter tw-leading-relaxed">
              {isConnected
                ? `Your ${isGoogle ? "Google" : "Outlook"} account is successfully connected. You are ready to import and sync your contacts. Click 'Disconnect' if you want to unlink your account.`
                : `You need to connect your ${isGoogle ? "Google" : "Outlook"} account to retrieve and sync your contacts. Click 'Connect' on the right to authorize access.`}
            </p>
          </div>
          <div className="tw-flex-shrink-0">
            {isConnected ? (
              <Button
                type="text"
                danger
                onClick={() => onDisconnect(selectedSource)}
                className="tw-h-10 tw-px-6 !tw-rounded-xl tw-font-bold tw-font-inter hover:!tw-bg-red-50"
              >
                Disconnect
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={() => onConnect(selectedSource)}
                className="tw-h-10 tw-px-6 !tw-bg-[#5D44FF] hover:!tw-bg-[#4F36F2] !tw-rounded-xl tw-font-bold tw-font-inter !tw-shadow-none"
              >
                Connect
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const AudienceUploadStep = ({
  fileInputRef,
  selectedFile,
  onFileChange,
  totalParsedRows,
}) => (
  <div className="tw-mx-auto tw-mt-2 tw-max-w-[985px]">
    <div>
      <h2 className="tw-m-0 tw-text-[18px] tw-font-semibold tw-text-slate-800 tw-font-inter">
        Upload your CSV
      </h2>
      <p className="tw-m-0 tw-mt-1 tw-text-[13.5px] tw-text-slate-500 tw-font-inter">
        We&apos;ll auto-detect columns in the next step.
      </p>
    </div>

    <div className="tw-mt-6">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx"
        onChange={onFileChange}
        className="tw-hidden"
      />

      {selectedFile ? (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="tw-flex tw-h-[280px] tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-[#5D44FF] tw-bg-[#5D44FF]/5 tw-transition-all tw-duration-300"
        >
          <div className="tw-flex tw-w-[430px] tw-h-[170px] tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-emerald-250 tw-bg-white tw-px-6 tw-text-center tw-shadow-sm">
            <div className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-xl tw-bg-[#EAFBF0] tw-text-[#16A34A] tw-text-xl tw-shadow-sm">
              <ExcelFileIcon />
            </div>
            <div className="tw-mt-3.5 tw-text-[14px] tw-font-bold tw-text-slate-800 tw-truncate tw-w-full tw-font-inter">
              {selectedFile.name}
            </div>
            <div className="tw-mt-1 tw-text-[12.5px] tw-font-medium tw-text-slate-500 tw-font-inter">
              {formatFileSize(selectedFile.size)} · {totalParsedRows !== undefined ? `${totalParsedRows} rows detected` : makeDetectedRowsText(selectedFile)}
            </div>
          </div>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="tw-flex tw-h-[280px] tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-rounded-2xl tw-border-2 tw-border-dashed tw-border-slate-200 tw-bg-white tw-p-6 tw-text-center hover:tw-border-[#5D44FF] hover:tw-bg-slate-50/50 tw-transition-all tw-duration-300"
        >
          <div className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-2xl tw-border tw-border-solid tw-border-[#5D44FF]/30 tw-bg-white tw-text-[#5D44FF] tw-text-[26px] tw-shadow-[0_8px_24px_rgba(93,68,255,0.08)]">
            <FolderUploadIcon />
          </div>
          <div className="tw-mt-4 tw-text-[18px] tw-font-bold tw-text-slate-800 tw-font-inter">
            Drop your CSV here
          </div>
          <div className="tw-mt-1.5 tw-text-[13.5px] tw-font-normal tw-text-slate-400 tw-font-inter">
            or click to browse from your computer
          </div>
          <Button
            type="primary"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            className="tw-mt-4 !tw-flex !tw-h-10 !tw-items-center !tw-gap-2 !tw-rounded-xl !tw-bg-[#5D44FF] hover:!tw-bg-[#4F36F2] !tw-border-0 !tw-px-6 !tw-text-[13.5px] !tw-font-bold !tw-font-inter !tw-shadow-none"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: "inline-block" }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="12" y1="18" x2="12" y2="12"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
            Browse files
          </Button>
          <div className="tw-mt-3.5 tw-text-[12px] tw-text-slate-400 tw-font-normal tw-font-inter">
            Supported: .csv (UTF-8). Up to 10,000+ rows.
          </div>
        </div>
      )}
    </div>

    <div className="tw-mt-6 tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-px-6 tw-py-4">
      <span className="tw-text-[14px] tw-font-bold tw-text-slate-750 tw-font-inter">
        Need a template?
      </span>
      <a
        href={sampleCsvHref}
        download="sample-audience.csv"
        className="tw-flex tw-items-center tw-gap-1.5 tw-text-[13.5px] tw-font-bold tw-text-[#5D44FF] hover:tw-opacity-80 tw-font-inter tw-transition-opacity"
      >
        <DownloadOutlined className="tw-text-sm" />
        Download Sample CSV
      </a>
    </div>
  </div>
);
export const AudienceManualPrepareStep = ({
  manualRows,
  onAddManualRow,
  onDeleteManualRow,
  onManualRowChange,
  audienceName,
  onAudienceNameChange,
}) => {
  const placeholders = [
    { name: "e.g. Jonathan Michael", phone: "e.g. 845478751212", email: "e.g. jonathan@example.com" },
    { name: "e.g. Michel Obama", phone: "e.g. 84542122121", email: "e.g. michel@example.com" },
    { name: "e.g. Jack Johnson", phone: "e.g. 98451545645", email: "e.g. jack@example.com" },
    { name: "e.g. Miley Cyrus", phone: "e.g. 487543548945", email: "e.g. miley@example.com" },
  ];

  return (
    <div className="tw-mx-auto tw-mt-2 tw-max-w-[985px]">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <div>
          <h2 className="tw-m-0 tw-text-[18px] tw-font-semibold tw-text-slate-800 tw-font-inter">
            Add your contacts
          </h2>
          <p className="tw-m-0 tw-mt-1 tw-text-[13px] tw-text-slate-500 tw-font-inter">
            Type - we&apos;ll validate them in real time.
          </p>
        </div>
      </div>

      <div className="tw-mt-4">
        <div className="tw-grid tw-grid-cols-[1.2fr_1.15fr_1.2fr_32px] tw-gap-4 tw-pb-2 tw-text-[11px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-font-inter">
          <div>Name</div>
          <div>
            Phone<span className="tw-text-red-500">*</span>
          </div>
          <div>
            Email<span className="tw-text-red-500">*</span>
          </div>
          <div />
        </div>

        <div className="tw-space-y-3 tw-max-h-[280px] tw-overflow-y-auto tw-pr-1 sleek-scrollbar">
          {manualRows.map((row, index) => {
            const ph = placeholders[index % placeholders.length];
            return (
              <div
                key={row.id}
                className="tw-grid tw-grid-cols-[1.2fr_1.15fr_1.2fr_32px] tw-items-center tw-gap-4"
              >
                <Input
                  value={row.name}
                  onChange={(event) => onManualRowChange(row.id, "name", event.target.value)}
                  className="!tw-h-10 !tw-rounded-lg !tw-border-slate-200 !tw-text-slate-800 !tw-text-[14px] !tw-font-medium !tw-font-inter placeholder:!tw-text-slate-400 placeholder:!tw-font-normal focus:!tw-border-[#5D44FF] focus:!tw-shadow-[0_0_0_2px_rgba(93,68,255,0.08)]"
                  placeholder={ph.name}
                />
                <Input
                  value={row.phone}
                  onChange={(event) => onManualRowChange(row.id, "phone", event.target.value)}
                  className="!tw-h-10 !tw-rounded-lg !tw-border-slate-200 !tw-text-slate-800 !tw-text-[14px] !tw-font-medium !tw-font-inter placeholder:!tw-text-slate-400 placeholder:!tw-font-normal focus:!tw-border-[#5D44FF] focus:!tw-shadow-[0_0_0_2px_rgba(93,68,255,0.08)]"
                  placeholder={ph.phone}
                />
                <Input
                  value={row.email}
                  onChange={(event) => onManualRowChange(row.id, "email", event.target.value)}
                  className="!tw-h-10 !tw-rounded-lg !tw-border-slate-200 !tw-text-slate-800 !tw-text-[14px] !tw-font-medium !tw-font-inter placeholder:!tw-text-slate-400 placeholder:!tw-font-normal focus:!tw-border-[#5D44FF] focus:!tw-shadow-[0_0_0_2px_rgba(93,68,255,0.08)]"
                  placeholder={ph.email}
                />
                <button
                  type="button"
                  onClick={() => onDeleteManualRow(row.id)}
                  className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-slate-400 hover:tw-bg-red-50 hover:tw-text-red-500 tw-transition-colors tw-cursor-pointer"
                >
                  <DeleteOutlined className="tw-text-sm" />
                </button>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={onAddManualRow}
          className="tw-mt-5 tw-border-0 tw-bg-transparent tw-p-0 tw-text-[13.5px] tw-font-bold tw-text-[#5D44FF] hover:tw-opacity-80 tw-font-inter tw-transition-opacity tw-cursor-pointer"
        >
          + Add Row
        </button>
      </div>
    </div>
  );
};;

export const AudienceMappingStep = ({ 
  columnMapping, 
  onColumnMappingChange,
  parsedHeaders = [],
  parsedPreviewRows = [],
  totalParsedRows = 0
}) => (
  <div className="tw-mx-auto tw-mt-6 tw-max-w-[985px] tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100 tw-bg-white tw-p-6 tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)]">
    <div>
      <h2 className="tw-m-0 tw-text-[20px] tw-font-semibold tw-text-slate-800 tw-font-poppins">
        Map your columns
      </h2>
      <p className="tw-m-0 tw-mt-1 tw-text-[13.5px] tw-text-slate-500 tw-font-inter">
        We auto-mapped fields based on your headers. Adjust as needed.
      </p>
    </div>

    <div className="tw-mt-5 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100 tw-bg-white tw-p-4 tw-shadow-[0_4px_16px_rgba(15,23,42,0.01)]">
      <div className="tw-mb-4 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-slate-100 tw-bg-slate-50/50 tw-px-4 tw-py-3.5">
        <span className="tw-inline-flex tw-rounded-lg tw-bg-[#EBE8FD] tw-px-3 tw-py-1 tw-text-[12px] tw-font-semibold tw-text-[#5D44FF] tw-font-poppins">
          {totalParsedRows} rows detected
        </span>
        <span className="tw-text-[12px] tw-font-medium tw-text-slate-400 tw-font-inter">
          Preview of first {parsedPreviewRows.length} rows
        </span>
      </div>

      <div className="tw-overflow-x-auto tw-rounded-xl tw-border tw-border-solid tw-border-slate-100 tw-bg-white tw-p-4 sleek-scrollbar">
        <div style={{ width: "fit-content", minWidth: "100%" }}>
          {/* Dropdowns row */}
          <div className="tw-flex tw-gap-3">
            {parsedHeaders.map((header, index) => (
              <div key={index} className="tw-w-[185px] tw-flex-shrink-0 tw-flex tw-items-center">
                <Select
                  value={columnMapping[header] || "ignore"}
                  onChange={(value) => onColumnMappingChange(header, value)}
                  options={columnOptions}
                  className="tw-w-full"
                  dropdownClassName="custom-select-dropdown"
                />
              </div>
            ))}
          </div>

          {/* Header row */}
          <div className="tw-flex tw-gap-3 tw-mt-3">
            {parsedHeaders.map((header, index) => (
              <div
                key={index}
                className="tw-w-[185px] tw-flex-shrink-0 tw-bg-slate-50 tw-rounded-lg tw-px-3.5 tw-py-3 tw-text-[12px] tw-font-bold tw-text-slate-700 tw-border tw-border-solid tw-border-slate-100 tw-truncate tw-font-inter"
                title={header}
              >
                {header}
              </div>
            ))}
          </div>

          {/* Data rows */}
          <div className="tw-mt-2">
            {parsedPreviewRows.map((row) => (
              <div
                key={row.id}
                className="tw-flex tw-gap-3 tw-text-[13px] tw-font-medium tw-text-slate-650 tw-py-4 tw-border-0 tw-border-b tw-border-solid tw-border-slate-100 hover:tw-bg-slate-50/30 tw-transition-colors"
              >
                {parsedHeaders.map((header, index) => (
                  <div key={index} className="tw-w-[185px] tw-flex-shrink-0 tw-px-3.5 tw-truncate tw-font-inter" title={row[header]}>
                    {row[header]}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const AudienceValidationStep = ({ 
  validationCards: customValidationCards, 
  jobId,
  stats,
  allowDuplicates,
  onAllowDuplicatesChange
}) => {
  const cardsToRender = customValidationCards || validationCards;
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (jobId) {
      fetchPreview();
    }
  }, [jobId]);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      const res = await getImportPreview(jobId, { page: 0, size: 20 });
      setPreviewData(res.data);
    } catch (error) {
      console.error("Failed to fetch preview data", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Phone", dataIndex: "phoneNumber", key: "phoneNumber" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Company", dataIndex: "company", key: "company" },
    { 
      title: "Status", 
      key: "status", 
      render: (_, record) => {
        if (record.invalid) return <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-red-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-red-750 tw-border tw-border-solid tw-border-red-150 tw-font-inter">Invalid</span>;
        if (record.duplicate) return <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-amber-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-amber-750 tw-border tw-border-solid tw-border-amber-150 tw-font-inter">Duplicate</span>;
        return <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-emerald-50 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-emerald-750 tw-border tw-border-solid tw-border-emerald-150 tw-font-inter">Valid</span>;
      }
    }
  ];

  return (
    <div className="tw-mx-auto tw-mt-6 tw-max-w-[985px] tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100 tw-bg-white tw-p-6 md:tw-p-8 tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)]">
      <div>
        <h2 className="tw-m-0 tw-text-[20px] tw-font-semibold tw-text-slate-800 tw-font-poppins">
          Validation summary
        </h2>
        <p className="tw-m-0 tw-mt-1 tw-text-[13.5px] tw-text-slate-500 tw-font-inter">
          Here&apos;s what your data looks like right now.
        </p>
      </div>

      <div className="tw-mt-6 tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-4">
        {cardsToRender.map((card) => (
          <ValidationCard key={card.label} card={card} />
        ))}
      </div>

      {stats?.duplicates > 0 && (
        <div className="tw-mt-8 tw-rounded-2xl tw-bg-amber-50/40 tw-p-6 tw-border tw-border-solid tw-border-amber-100 tw-shadow-[0_4px_16px_rgba(245,158,11,0.015)]">
          <h3 className="tw-m-0 tw-text-[16px] tw-font-bold tw-text-amber-900 tw-flex tw-items-center tw-gap-2.5 tw-font-inter">
            <WarningOutlined className="tw-text-amber-500 tw-text-lg" />
            Duplicate Contacts Found
          </h3>
          <p className="tw-mt-2 tw-text-[13.5px] tw-text-slate-600 tw-leading-relaxed tw-font-inter">
            We found {stats.duplicates} contacts with phone numbers that already exist in this audience or within your upload. How would you like to handle them?
          </p>
          
          <div className="tw-mt-5 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <div
              onClick={() => onAllowDuplicatesChange(false)}
              className={`tw-cursor-pointer tw-flex tw-gap-4 tw-p-5 tw-rounded-xl tw-border tw-border-solid tw-transition-all ${
                allowDuplicates === false
                  ? "tw-bg-[#F9F8FF] tw-border-[#5D44FF] tw-shadow-[0_4px_16px_rgba(93,68,255,0.08)]"
                  : "tw-bg-white tw-border-slate-200 hover:tw-border-slate-300 hover:tw-shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
              }`}
              style={{ alignItems: 'flex-start' }}
            >
              {/* Radio Indicator */}
              <div className="tw-flex-shrink-0" style={{ marginTop: '2.5px' }}>
                <div className={`tw-h-5 tw-w-5 tw-rounded-full tw-border tw-border-solid tw-flex tw-items-center tw-justify-center tw-transition-all ${
                  allowDuplicates === false
                    ? "tw-border-[#5D44FF] tw-bg-[#5D44FF]"
                    : "tw-border-slate-300 tw-bg-white"
                }`}>
                  {allowDuplicates === false && (
                    <div className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-white" />
                  )}
                </div>
              </div>
              
              {/* Text Block */}
              <div>
                <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2">
                  <span className="tw-text-[14.5px] tw-font-bold tw-text-slate-800 tw-font-inter">
                    Skip duplicates
                  </span>
                  <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-emerald-50 tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-bold tw-text-emerald-700 tw-border tw-border-solid tw-border-emerald-150 tw-font-inter tw-uppercase tw-tracking-wider">
                    Recommended
                  </span>
                </div>
                <p className="tw-m-0 tw-mt-1.5 tw-text-[12.5px] tw-text-slate-500 tw-leading-relaxed tw-font-inter">
                  We will only import the new contacts, leaving the existing ones untouched.
                </p>
              </div>
            </div>

            {/* Option 2: Import Duplicates */}
            <div
              onClick={() => onAllowDuplicatesChange(true)}
              className={`tw-cursor-pointer tw-flex tw-gap-4 tw-p-5 tw-rounded-xl tw-border tw-border-solid tw-transition-all ${
                allowDuplicates === true
                  ? "tw-bg-[#F9F8FF] tw-border-[#5D44FF] tw-shadow-[0_4px_16px_rgba(93,68,255,0.08)]"
                  : "tw-bg-white tw-border-slate-200 hover:tw-border-slate-300 hover:tw-shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
              }`}
              style={{ alignItems: 'flex-start' }}
            >
              {/* Radio Indicator */}
              <div className="tw-flex-shrink-0" style={{ marginTop: '2.5px' }}>
                <div className={`tw-h-5 tw-w-5 tw-rounded-full tw-border tw-border-solid tw-flex tw-items-center tw-justify-center tw-transition-all ${
                  allowDuplicates === true
                    ? "tw-border-[#5D44FF] tw-bg-[#5D44FF]"
                    : "tw-border-slate-300 tw-bg-white"
                }`}>
                  {allowDuplicates === true && (
                    <div className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-white" />
                  )}
                </div>
              </div>
              
              {/* Text Block */}
              <div>
                <span className="tw-text-[14.5px] tw-font-bold tw-text-slate-800 tw-font-inter tw-block">
                  Import duplicates
                </span>
                <p className="tw-m-0 tw-mt-1.5 tw-text-[12.5px] tw-text-slate-500 tw-leading-relaxed tw-font-inter">
                  We will import all contacts, potentially creating duplicate entries in the database.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {jobId && (
        <div className="tw-mt-8">
          <h3 className="tw-m-0 tw-mb-4 tw-text-[16px] tw-font-bold tw-text-slate-800 tw-font-poppins">
            Import Preview (First 20 records)
          </h3>
          <div className="tw-rounded-xl tw-border tw-border-solid tw-border-slate-100 tw-overflow-hidden tw-bg-white tw-shadow-[0_4px_16px_rgba(15,23,42,0.01)]">
            <Table 
              dataSource={previewData} 
              columns={columns} 
              rowKey="id" 
              pagination={false} 
              loading={loading}
              size="small"
              scroll={{ y: 180 }}
              className="tw-custom-table-no-border"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const AudienceReviewStep = ({
  audienceName,
  audienceDescription = "",
  audienceTags = [],
  onAudienceNameChange,
  onAudienceDescriptionChange,
  onAudienceTagsChange,
  availableTags = [],
  visibility,
  onVisibilityChange,
  isManualSource = false,
  displayRows = [],
  stats,
}) => {
  const [localTagInput, setLocalTagInput] = useState("");

  const finalStats = stats || {
    total: isManualSource ? 4 : 2859,
    whatsapp: isManualSource ? 4 : 895,
    email: isManualSource ? 4 : 1000,
    invalid: 0,
  };

  const handleAddLocalTag = () => {
    const trimmed = localTagInput.trim();
    if (!trimmed) return;
    if (!audienceTags.includes(trimmed)) {
      onAudienceTagsChange?.([...audienceTags, trimmed]);
    }
    setLocalTagInput("");
  };

  const handleRemoveLocalTag = (tagToRemove) => {
    onAudienceTagsChange?.(audienceTags.filter(t => t !== tagToRemove));
  };

  const handleToggleVisibility = () => {
    onVisibilityChange?.(visibility === "public" ? "private" : "public");
  };

  return (
    <div className="tw-mx-auto tw-mt-6 tw-max-w-[985px] tw-space-y-6">
      <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-slate-100 tw-bg-white tw-p-6 md:tw-p-8 tw-shadow-[0_10px_30px_rgba(0,0,0,0.02)]">
        <div>
          <h2 className="tw-m-0 tw-text-[20px] tw-font-semibold tw-text-slate-800 tw-font-poppins">
            Final details
          </h2>
          <p className="tw-m-0 tw-mt-1 tw-text-[13.5px] tw-text-slate-500 tw-font-inter">
            Name your audience, write a description, and add tags.
          </p>
        </div>

        <div className="tw-mt-6 tw-space-y-6">
          <div>
            <label className="tw-mb-1.5 tw-block tw-text-[13px] tw-font-bold tw-text-slate-700 tw-font-poppins tw-uppercase tw-tracking-wider">
              Audience Name
            </label>
            <Input
              value={audienceName}
              onChange={(event) => onAudienceNameChange(event.target.value)}
              placeholder="e.g. Q4 Newsletter"
              className="!tw-h-11 !tw-rounded-lg !tw-border-slate-200 !tw-text-slate-800 !tw-text-[14px] !tw-font-medium !tw-font-inter focus:!tw-border-[#5D44FF] focus:!tw-shadow-[0_0_0_2px_rgba(93,68,255,0.08)]"
            />
          </div>

          <div>
            <label className="tw-mb-1.5 tw-block tw-text-[13px] tw-font-bold tw-text-slate-700 tw-font-poppins tw-uppercase tw-tracking-wider">
              Description
            </label>
            <Input.TextArea
              value={audienceDescription}
              onChange={(event) => onAudienceDescriptionChange?.(event.target.value)}
              placeholder="Add notes or description about this audience..."
              rows={3}
              className="!tw-rounded-lg !tw-border-slate-200 !tw-text-slate-800 !tw-text-[14px] !tw-font-medium !tw-font-inter focus:!tw-border-[#5D44FF] focus:!tw-shadow-[0_0_0_2px_rgba(93,68,255,0.08)]"
            />
          </div>

          <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-[1fr_auto] tw-items-end">
            <div>
              <label className="tw-mb-1.5 tw-block tw-text-[13px] tw-font-bold tw-text-slate-700 tw-font-poppins tw-uppercase tw-tracking-wider">
                Tags
              </label>
              <div className="tw-mt-1.5 tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                <Input
                  value={localTagInput}
                  onChange={(e) => setLocalTagInput(e.target.value)}
                  onPressEnter={handleAddLocalTag}
                  placeholder="Select or type tags and press enter..."
                  className="!tw-h-11 !tw-w-[340px] !tw-rounded-xl !tw-border-slate-250 !tw-text-slate-800 !tw-text-[14px] !tw-font-medium !tw-font-inter focus:!tw-border-[#5D44FF] focus:!tw-shadow-[0_0_0_2px_rgba(93,68,255,0.08)]"
                />
                <Button
                  onClick={handleAddLocalTag}
                  className="!tw-h-11 !tw-w-11 !tw-flex !tw-items-center !tw-justify-center !tw-bg-[#F1F3F5] hover:!tw-bg-[#E2E6EA] !tw-border-slate-250 !tw-rounded-xl !tw-text-slate-750 !tw-text-lg !tw-font-bold"
                >
                  +
                </Button>
                <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                  {audienceTags.map((tag) => (
                    <div
                      key={tag}
                      className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-white tw-border tw-border-solid tw-border-slate-200/90 tw-rounded-full tw-px-3.5 tw-py-1.5 tw-text-xs tw-font-bold tw-text-slate-800 tw-shadow-xs"
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="tw-text-slate-400 tw-flex-shrink-0"
                      >
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                        <line x1="7" y1="7" x2="7.01" y2="7"></line>
                      </svg>
                      <span>{tag}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveLocalTag(tag)}
                        className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-slate-400 hover:tw-text-rose-500 tw-cursor-pointer tw-ml-1 tw-flex tw-items-center tw-justify-center tw-text-xs"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="tw-flex tw-items-center tw-h-11 tw-pl-4">
              <div className="tw-flex tw-items-center tw-gap-3">
                <span className="tw-text-[13.5px] tw-font-semibold tw-text-slate-600 tw-font-poppins">Public</span>
                <Switch
                  checked={visibility === "private"}
                  onChange={(checked) => onVisibilityChange?.(checked ? "private" : "public")}
                />
                <span className="tw-text-[13.5px] tw-font-semibold tw-text-slate-600 tw-font-poppins">Private</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-space-y-3.5 tw-mt-6">
        <div className="tw-flex tw-items-center tw-justify-between">
          <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-[15px] tw-font-bold tw-text-slate-800 tw-font-poppins">
            <EyeOutlined className="tw-text-base tw-text-[#5D44FF]" /> Final Preview Summary
          </span>
          <span className="tw-text-xs tw-font-medium tw-text-slate-500">
            Live audience breakdown
          </span>
        </div>

        <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-3.5">
          {/* Total Contacts */}
          <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-xl tw-p-4 tw-shadow-2xs hover:tw-shadow-sm tw-transition-shadow">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <span className="tw-text-[11px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Total</span>
              <div className="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center">
                <UserOutlined className="tw-text-indigo-600 tw-text-xs" />
              </div>
            </div>
            <div className="tw-text-2xl tw-font-black tw-text-slate-800 tw-font-poppins">
              {finalStats.total.toLocaleString()}
            </div>
            <div className="tw-text-[11px] tw-text-slate-500 tw-mt-1">All detected contacts</div>
          </div>

          {/* Email Reachable */}
          <div className="tw-bg-indigo-50/40 tw-border tw-border-solid tw-border-indigo-100 tw-rounded-xl tw-p-4 tw-shadow-2xs hover:tw-shadow-sm tw-transition-shadow">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <span className="tw-text-[11px] tw-font-bold tw-text-indigo-700 tw-uppercase tw-tracking-wider">Email</span>
              <div className="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-indigo-100/80 tw-flex tw-items-center tw-justify-center">
                <MailOutlined className="tw-text-[#5D44FF] tw-text-xs" />
              </div>
            </div>
            <div className="tw-text-2xl tw-font-black tw-text-[#5D44FF] tw-font-poppins">
              {finalStats.email.toLocaleString()}
            </div>
            <div className="tw-text-[11px] tw-text-indigo-600/80 tw-mt-1">Valid email addresses</div>
          </div>

          {/* WhatsApp Validated */}
          <div className="tw-bg-emerald-50/40 tw-border tw-border-solid tw-border-emerald-100 tw-rounded-xl tw-p-4 tw-shadow-2xs hover:tw-shadow-sm tw-transition-shadow">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <span className="tw-text-[11px] tw-font-bold tw-text-emerald-700 tw-uppercase tw-tracking-wider">WhatsApp</span>
              <div className="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-emerald-100/80 tw-flex tw-items-center tw-justify-center">
                <WhatsAppOutlined className="tw-text-emerald-600 tw-text-xs" />
              </div>
            </div>
            <div className="tw-text-2xl tw-font-black tw-text-emerald-600 tw-font-poppins">
              {finalStats.whatsapp.toLocaleString()}
            </div>
            <div className="tw-text-[11px] tw-text-emerald-600/80 tw-mt-1">Active phone numbers</div>
          </div>

          {/* Invalid Removed */}
          <div className="tw-bg-rose-50/40 tw-border tw-border-solid tw-border-rose-100 tw-rounded-xl tw-p-4 tw-shadow-2xs hover:tw-shadow-sm tw-transition-shadow">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <span className="tw-text-[11px] tw-font-bold tw-text-rose-700 tw-uppercase tw-tracking-wider">Invalid</span>
              <div className="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-rose-100/80 tw-flex tw-items-center tw-justify-center">
                <CloseCircleOutlined className="tw-text-rose-500 tw-text-xs" />
              </div>
            </div>
            <div className="tw-text-2xl tw-font-black tw-text-rose-500 tw-font-poppins">
              {finalStats.invalid.toLocaleString()}
            </div>
            <div className="tw-text-[11px] tw-text-rose-600/80 tw-mt-1">Filtered invalid entries</div>
          </div>

          {/* Duplicates Removed */}
          <div className="tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-200/80 tw-rounded-xl tw-p-4 tw-shadow-2xs hover:tw-shadow-sm tw-transition-shadow">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <span className="tw-text-[11px] tw-font-bold tw-text-slate-500 tw-uppercase tw-tracking-wider">Duplicates</span>
              <div className="tw-w-7 tw-h-7 tw-rounded-lg tw-bg-slate-200/70 tw-flex tw-items-center tw-justify-center">
                <CopyOutlined className="tw-text-slate-600 tw-text-xs" />
              </div>
            </div>
            <div className="tw-text-2xl tw-font-black tw-text-slate-700 tw-font-poppins">
              0
            </div>
            <div className="tw-text-[11px] tw-text-slate-500 tw-mt-1">Deduplicated records</div>
          </div>
        </div>
      </div>
    </div>
  );
};
